/**
 * Button Component
 */

import React, { forwardRef } from 'react';
import {
  ButtonProps,
  ButtonShapes,
  ButtonSizes,
  buttonSizeMap,
  ButtonStyles,
  ButtonTypes,
  IndividualButtonSizes,
  ButtonRef,
} from './button.types';
import Icon from '../Icon/icon';
import { withTheme } from 'styled-components';
import { ButtonColors, IndividualButtonColors, retrieveTeamColorMap } from './button.colors';
import { StyledButton, Label } from './button.style';

import COLORS from '../../style/universal.color';
const { linkBlue } = COLORS;

// Returns a simple string for qualifiying button types
const retrieveStyleButtonType = (style: ButtonStyles): string => {
  switch (style) {
    case ButtonStyles.DEFAULT:
      return 'default';
    case ButtonStyles.TICKET_LINK_GREEN:
      return 'ticketGreen';
    case ButtonStyles.MLB_TV_ORANGE:
      return 'mlbTvOrange';
    default:
      return 'linkBlue';
  }
}

const Button = forwardRef<ButtonRef, ButtonProps>(
  (props: ButtonProps, ref ) => {
  const {
    disabled = false,
    value,
    role = 'button',
    additionalProps = {},
    size = ButtonSizes.SMALL,
    label,
    styleName = ButtonStyles.LINK_BLUE,
    handlers = {},
    children,
    iconName,
    iconLeft = true,
    shape = ButtonShapes.SOLID,
    type = ButtonTypes.BUTTON,
    hasHover = true,
    hoverSpeed,
    className,
    ariaLabel,
    iconHoverColor,
    iconSize,
    theme = {},
    dataAttr = {},
    id,
    active = false,
    ariaPressed = false,
  } = props;

  let { iconColor } = props;
  const { dark, teamColor1 } = theme;

  let hSpeed: number;
  if ( hoverSpeed !== undefined ) {
    hSpeed = hoverSpeed;
  } else if ( theme.hoverSpeed !== undefined ) {
    hSpeed = theme.hoverSpeed;
  } else {
    hSpeed = 0.2;
  }

  // Establish the specfic version of the button
  const themeType: string = dark ? 'dark' : 'light';
  const hollowMode: string = shape !== ButtonShapes.SOLID ? 'hollow' : 'solid';
  const buttonType: string = disabled ? 'disabled': retrieveStyleButtonType(styleName);
  const borderless: boolean = shape === ButtonShapes.HOLLOW_BORDERLESS;
  const colorMap: IndividualButtonColors = styleName === ButtonStyles.TEAM ?
    retrieveTeamColorMap(teamColor1 || linkBlue, hollowMode, themeType) :
    ButtonColors[themeType][hollowMode][buttonType];

  // Retrieve the values from the color map
  const {
    color,
    backgroundColor,
    borderColor,
    hoverBackgroundColor,
    hoverBorderColor,
    hoverColor,
  } = colorMap;

  // Establish if only an icon was provided which will tailor the padding in the button
  const onlyIcon: boolean = !(label || children);

  // Retrieve a map of sizes required by the button (height, padding, etc.);
  const sizeStyles: IndividualButtonSizes = buttonSizeMap[size];

  iconColor = iconColor || color;

  // Call the handler when the event is triggered
  const invokeHandler: Function = (t: string, e?: Event) => {
    handlers[t](e, value);
  };

  // Construct an object of props which will contain all aria labels, the value, handlers, etc.
  const completeButtonProps: object = {
    'aria-label': ariaLabel || label,
    'aria-pressed': ariaPressed,
    role,
    value,
    type,
    className,
    disabled,
    ref,
    ...dataAttr,
    ...additionalProps
  };
  Object.keys(handlers).forEach((key: string) => {
    completeButtonProps[key] = (e: Event) => {
      if (!disabled) {
        invokeHandler(key, e);
      }
    };
  });

  const iconWidth = iconSize || sizeStyles['iconSize'];

  return (
    <StyledButton
      sizeStyles={ sizeStyles }
      hSpeed={ hSpeed }
      shape={ shape }
      backgroundColor={ backgroundColor }
      borderless={ borderless }
      borderColor={ borderColor }
      onlyIcon={ onlyIcon }
      color={ color }
      iconColor={ iconColor }
      icon={ !!iconName }
      iconLeft={ iconLeft }
      disabled={ disabled }
      hasHover={ hasHover }
      hoverBackgroundColor={ hoverBackgroundColor }
      hoverColor={ hoverColor }
      hoverBorderColor={ hoverBorderColor }
      iconHoverColor={ iconHoverColor }
      styleName={ styleName }
      id={ id }
      active={ active }
      {...completeButtonProps}
    >
      { iconLeft && iconName && <Icon width={ iconWidth } name={ iconName } fill={iconColor} /> }
      <Label>{ label || children}</Label>
      { !iconLeft && iconName && <Icon width={ iconWidth } name={ iconName } fill={iconColor} /> }
    </StyledButton>
  );
});

export default withTheme(Button);
